import React from 'react';

import { ArrowLeft, ArrowRight } from 'lucide-react';

import { IMcsaQuestion, IMcsaWorld, TMcsaAnswer } from '../../../../types/mcsa.interface';
import { MCSA_JOURNEY_COPY } from '../../../../helpers/mcsa-journey.copy';
import { MCSA_WORLD_THEME } from '../../../../helpers/mcsa-world.theme';
import {
    getAnswerLabel,
    isDualAnswer,
    isQuestionAnswered,
} from '../../../../helpers/mcsa-journey.helper';

interface JourneyQuestionProps {
    world: IMcsaWorld;
    questions: IMcsaQuestion[];
    questionIndex: number;
    answers: Record<number, TMcsaAnswer>;
    shopRemaining?: number;
    secondsLeft?: number;
    ringPct?: number;
    onPick: (value: number) => void;
    onDual: (field: 'like' | 'risk', value: number) => void;
    onNext: () => void;
    onBack: () => void;
    onBackToMap: () => void;
}

export const JourneyQuestion = (props: JourneyQuestionProps) => {
    const {
        world,
        questions,
        questionIndex,
        answers,
        shopRemaining,
        secondsLeft = 9,
        ringPct = 0,
        onPick,
        onDual,
        onNext,
        onBack,
        onBackToMap,
    } = props;

    const question = questions[questionIndex];
    const answer = answers[questionIndex];
    const theme = MCSA_WORLD_THEME[world.key];
    const answered = isQuestionAnswered(world.question_mech, answer);
    const isLast = questionIndex >= questions.length - 1;
    const nextLabel = isLast ? MCSA_JOURNEY_COPY.finish_theme : MCSA_JOURNEY_COPY.next;
    const numericAnswer = typeof answer === 'number' ? answer : undefined;

    return (
        <div className={`mcsa_journey_stage mcsa_journey_stage--${world.key}`}>
            <div className='mcsa_journey_stage__top'>
                <button type='button' className='mcsa_journey_stage__map' onClick={onBackToMap}>
                    ← {MCSA_JOURNEY_COPY.back_to_map_short}
                </button>
                <span>
                    {world.name} · {world.question_mech === 'swipe'
                        ? `${shopRemaining ?? questions.length} ${MCSA_JOURNEY_COPY.left_label}`
                        : `${questionIndex + 1}/${questions.length}`}
                </span>
            </div>
            <div className='mcsa_journey_stage__dots'>
                {questions.map((_, index) => {
                    const answeredDot = isQuestionAnswered(world.question_mech, answers[index]);
                    return (
                        <span
                            key={index}
                            className={`mcsa_journey_stage__dot${answeredDot ? ' mcsa_journey_stage__dot--done' : ''}${index === questionIndex ? ' mcsa_journey_stage__dot--current' : ''}`}
                            style={answeredDot ? { background: theme.accent } : undefined}
                        />
                    );
                })}
            </div>

            {world.question_mech === 'slider' && (
                <SliderMech
                    question={question}
                    answer={numericAnswer}
                    accent={theme.accent}
                    onPick={onPick}
                />
            )}
            {world.question_mech === 'stamp' && (
                <StampMech
                    question={question}
                    answer={numericAnswer}
                    accent={theme.accent}
                    onPick={onPick}
                />
            )}
            {world.question_mech === 'swipe' && (
                <SwipeMech
                    question={question}
                    answer={numericAnswer}
                    accent={theme.accent}
                    secondsLeft={secondsLeft}
                    ringPct={ringPct}
                    onPick={onPick}
                />
            )}
            {world.question_mech === 'thermo' && (
                <ThermoMech
                    question={question}
                    answer={numericAnswer}
                    accent={theme.accent}
                    accent2={theme.accent2}
                    onPick={onPick}
                />
            )}
            {world.question_mech === 'chat' && (
                <ChatMech
                    question={question}
                    answer={numericAnswer}
                    accent={theme.accent}
                    accent2={theme.accent2}
                    onPick={onPick}
                />
            )}
            {world.question_mech === 'dual' && (
                <DualMech
                    question={question}
                    answer={isDualAnswer(answer) ? answer : {}}
                    accent={theme.accent}
                    onDual={onDual}
                />
            )}

            {world.question_mech !== 'swipe' && (
                <div className='mcsa_journey_stage__nav'>
                    <button type='button' className='mcsa_journey_stage__back' onClick={onBack}>
                        <ArrowLeft />
                        {world.question_mech === 'stamp' ? MCSA_JOURNEY_COPY.previous : MCSA_JOURNEY_COPY.back}
                    </button>
                    <button
                        type='button'
                        className='mcsa_journey_stage__next'
                        disabled={!answered}
                        style={{
                            background: answered ? theme.accent : '#E5E7EB',
                            color: answered ? '#fff' : '#9CA3AF',
                            boxShadow: answered ? '0 10px 22px rgba(74,48,109,.18)' : 'none',
                        }}
                        onClick={onNext}
                    >
                        <span>{nextLabel}</span>
                        <ArrowRight />
                    </button>
                </div>
            )}
        </div>
    );
};

const ProgressHint = ({ answered, label, hint, accent }: { answered: boolean; label?: string; hint: string; accent: string }) => (
    <div className='mcsa_journey_q__hint'>
        {answered && label ? (
            <span style={{ color: accent }}>{label}</span>
        ) : (
            <span className='mcsa_journey_q__hint--empty'>{hint}</span>
        )}
    </div>
);

const SliderMech = ({
    question,
    answer,
    accent,
    onPick,
}: {
    question: IMcsaQuestion;
    answer?: number;
    accent: string;
    onPick: (value: number) => void;
}) => {
    const fill = answer === undefined ? 0 : (answer / 3) * 100;
    const label = answer === undefined ? undefined : getAnswerLabel(MCSA_JOURNEY_COPY.bipolar_labels, question, answer);

    return (
        <div className='mcsa_journey_q mcsa_journey_q--slider'>
            <p className='mcsa_journey_q__eyebrow'>{question.eyebrow}</p>
            <h2>{question.stem}</h2>
            <div className='mcsa_journey_q__card'>
                <div className='mcsa_journey_q__poles'>
                    <span>{question.a}</span>
                    <span>{question.b}</span>
                </div>
                <div className='mcsa_journey_q__track'>
                    <div className='mcsa_journey_q__track__base mcsa_journey_q__track__base--slider' />
                    <div className='mcsa_journey_q__track__fill' style={{ width: `${fill}%`, background: accent }} />
                    <div className='mcsa_journey_q__stops'>
                        {[0, 1, 2, 3].map(index => {
                            const selected = answer === index;
                            return (
                                <button
                                    key={index}
                                    type='button'
                                    className={`mcsa_journey_q__stop${selected ? ' mcsa_journey_q__stop--on' : ''}`}
                                    style={selected ? { background: accent, borderColor: accent } : undefined}
                                    onClick={() => onPick(index)}
                                />
                            );
                        })}
                    </div>
                </div>
                <ProgressHint answered={answer !== undefined} label={label} hint={MCSA_JOURNEY_COPY.slider_hint} accent={accent} />
            </div>
        </div>
    );
};

const StampMech = ({
    question,
    answer,
    accent,
    onPick,
}: {
    question: IMcsaQuestion;
    answer?: number;
    accent: string;
    onPick: (value: number) => void;
}) => {
    const label = answer === undefined ? undefined : getAnswerLabel(MCSA_JOURNEY_COPY.bipolar_labels, question, answer);

    return (
        <div className='mcsa_journey_q mcsa_journey_q--stamp'>
            <div className='mcsa_journey_q__pass'>
                <div className='mcsa_journey_q__pass__head' style={{ background: accent }}>
                    <span>{MCSA_JOURNEY_COPY.boarding_pass}</span>
                    <span>{MCSA_JOURNEY_COPY.airline}</span>
                </div>
                <div className='mcsa_journey_q__pass__body'>
                    <p className='mcsa_journey_q__eyebrow'>{question.eyebrow}</p>
                    <h2>{question.stem}</h2>
                    <div className='mcsa_journey_q__poles'>
                        <span>{question.a}</span>
                        <span>{question.b}</span>
                    </div>
                    <div className='mcsa_journey_q__stamps'>
                        {[0, 1, 2, 3].map(index => {
                            const selected = answer === index;
                            return (
                                <button key={index} type='button' className='mcsa_journey_q__stamp' onClick={() => onPick(index)}>
                                    {selected && (
                                        <span className='mcsa_journey_q__stamp__mark' style={{ borderColor: accent, color: accent }}>
                                            {MCSA_JOURNEY_COPY.bipolar_labels[index]}
                                        </span>
                                    )}
                                    <span
                                        className={`mcsa_journey_q__stamp__dot${selected ? ' mcsa_journey_q__stamp__dot--on' : ''}`}
                                        style={selected ? { background: accent, borderColor: accent } : undefined}
                                    />
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className='mcsa_journey_q__pass__foot'>
                    <ProgressHint answered={answer !== undefined} label={label ? `✦ ${label}` : undefined} hint={MCSA_JOURNEY_COPY.stamp_hint} accent={accent} />
                </div>
            </div>
        </div>
    );
};

const SwipeMech = ({
    question,
    answer,
    accent,
    secondsLeft,
    ringPct,
    onPick,
}: {
    question: IMcsaQuestion;
    answer?: number;
    accent: string;
    secondsLeft: number;
    ringPct: number;
    onPick: (value: number) => void;
}) => {
    const warn = secondsLeft <= 3;
    const ringColor = warn ? '#E4405F' : '#F0B429';

    return (
        <div className='mcsa_journey_q mcsa_journey_q--swipe'>
            <div
                className='mcsa_journey_q__ring'
                style={{
                    background: `conic-gradient(${ringColor} ${ringPct * 360}deg, rgba(255,255,255,.35) 0)`,
                    boxShadow: `0 0 42px ${warn ? 'rgba(228,64,95,.55)' : 'rgba(240,180,41,.4)'}`,
                }}
            >
                <div className='mcsa_journey_q__card mcsa_journey_q__card--swipe'>
                    <div className='mcsa_journey_q__swipe_head'>
                        <p className='mcsa_journey_q__eyebrow'>{question.eyebrow}</p>
                        <span style={{ color: ringColor }}>⏱ {secondsLeft}s</span>
                    </div>
                    <p className='mcsa_journey_q__stmt'>“{question.stmt}”</p>
                    <div className='mcsa_journey_q__chips'>
                        {MCSA_JOURNEY_COPY.shop_opts.map((label, index) => {
                            const selected = answer === index;
                            return (
                                <button
                                    key={label}
                                    type='button'
                                    className={`mcsa_journey_q__chip${selected ? ' mcsa_journey_q__chip--on' : ''}`}
                                    style={selected ? { background: accent, borderColor: accent, color: '#fff' } : undefined}
                                    onClick={() => onPick(index)}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                    <p className='mcsa_journey_q__hint--empty'>{MCSA_JOURNEY_COPY.swipe_hint}</p>
                </div>
            </div>
        </div>
    );
};

const ThermoMech = ({
    question,
    answer,
    accent,
    accent2,
    onPick,
}: {
    question: IMcsaQuestion;
    answer?: number;
    accent: string;
    accent2: string;
    onPick: (value: number) => void;
}) => {
    const fill = answer === undefined ? 0 : (answer / 3) * 100;
    const label = answer === undefined ? undefined : getAnswerLabel(MCSA_JOURNEY_COPY.bipolar_labels, question, answer);

    return (
        <div className='mcsa_journey_q mcsa_journey_q--thermo'>
            <p className='mcsa_journey_q__eyebrow'>{question.eyebrow}</p>
            <h2>{question.stem}</h2>
            <div className='mcsa_journey_q__card'>
                <div className='mcsa_journey_q__poles'>
                    <span>
                        <small>{MCSA_JOURNEY_COPY.thermo_left}</small>
                        {question.a}
                    </span>
                    <span>
                        <small>{MCSA_JOURNEY_COPY.thermo_right}</small>
                        {question.b}
                    </span>
                </div>
                <div className='mcsa_journey_q__track mcsa_journey_q__track--thermo'>
                    <div className='mcsa_journey_q__track__base' />
                    <div
                        className='mcsa_journey_q__track__fill'
                        style={{ width: `${fill}%`, background: `linear-gradient(90deg, ${accent2}, ${accent})` }}
                    />
                    <div className='mcsa_journey_q__stops'>
                        {[0, 1, 2, 3].map(index => {
                            const selected = answer === index;
                            return (
                                <button key={index} type='button' className='mcsa_journey_q__knob' onClick={() => onPick(index)}>
                                    <span
                                        className={selected ? 'mcsa_journey_q__knob__dot mcsa_journey_q__knob__dot--on' : 'mcsa_journey_q__knob__dot'}
                                        style={selected ? { background: `linear-gradient(140deg, ${accent}, ${accent2})` } : undefined}
                                    />
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <ProgressHint answered={answer !== undefined} label={label} hint={MCSA_JOURNEY_COPY.thermo_hint} accent={accent} />
        </div>
    );
};

const ChatMech = ({
    question,
    answer,
    accent,
    accent2,
    onPick,
}: {
    question: IMcsaQuestion;
    answer?: number;
    accent: string;
    accent2: string;
    onPick: (value: number) => void;
}) => (
    <div className='mcsa_journey_q mcsa_journey_q--chat'>
        <p className='mcsa_journey_q__eyebrow'>{question.eyebrow}</p>
        <div className='mcsa_journey_q__card mcsa_journey_q__card--chat'>
            <div className='mcsa_journey_q__bubble_row'>
                <span className='mcsa_journey_q__avatar' style={{ background: `linear-gradient(140deg, ${accent}, ${accent2})` }}>☺</span>
                <p className='mcsa_journey_q__bubble'>“{question.stmt}”</p>
            </div>
            <div className='mcsa_journey_q__reply'>
                {answer !== undefined && (
                    <span style={{ background: `linear-gradient(140deg, ${accent}, ${accent2})` }}>
                        {MCSA_JOURNEY_COPY.chat_opts[answer]}
                    </span>
                )}
            </div>
            <div className='mcsa_journey_q__chat_opts'>
                {MCSA_JOURNEY_COPY.chat_opts.map((label, index) => {
                    const selected = answer === index;
                    return (
                        <button
                            key={label}
                            type='button'
                            className={selected ? 'mcsa_journey_q__chat_opt mcsa_journey_q__chat_opt--on' : 'mcsa_journey_q__chat_opt'}
                            style={selected ? { borderColor: accent, color: accent } : undefined}
                            onClick={() => onPick(index)}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>
        </div>
    </div>
);

const DualMech = ({
    question,
    answer,
    accent,
    onDual,
}: {
    question: IMcsaQuestion;
    answer: { like?: number; risk?: number };
    accent: string;
    onDual: (field: 'like' | 'risk', value: number) => void;
}) => {
    const mismatch = answer.like != null && answer.risk != null && answer.like <= 1 && answer.risk <= 1;

    return (
        <div className='mcsa_journey_q mcsa_journey_q--dual'>
            <p className='mcsa_journey_q__eyebrow'>{question.eyebrow}</p>
            <div className='mcsa_journey_q__card'>
                <p className='mcsa_journey_q__stmt'>{question.stmt}</p>
            </div>
            <div className='mcsa_journey_q__dials'>
                <Dial
                    caption={MCSA_JOURNEY_COPY.like_caption}
                    opts={MCSA_JOURNEY_COPY.like_opts}
                    value={answer.like}
                    accent={accent}
                    top='#7488D6'
                    bottom='#5B6FC9'
                    onPick={value => onDual('like', value)}
                />
                <span className='mcsa_journey_q__dials__rule' />
                <Dial
                    caption={MCSA_JOURNEY_COPY.risk_caption}
                    opts={MCSA_JOURNEY_COPY.risk_opts}
                    value={answer.risk}
                    accent='#C0552F'
                    top='#E08A4E'
                    bottom='#C0552F'
                    onPick={value => onDual('risk', value)}
                />
            </div>
            {mismatch && <p className='mcsa_journey_q__dual_hint' style={{ color: accent }}>{MCSA_JOURNEY_COPY.dual_hint}</p>}
        </div>
    );
};

const Dial = ({
    caption,
    opts,
    value,
    accent,
    top,
    bottom,
    onPick,
}: {
    caption: string;
    opts: readonly string[];
    value?: number;
    accent: string;
    top: string;
    bottom: string;
    onPick: (value: number) => void;
}) => {
    const fillH = value == null ? 0 : 100 - (value / 3) * 100;

    return (
        <div className='mcsa_journey_q__dial'>
            <p style={{ color: accent }}>{caption}</p>
            <small>↑ {opts[0]}</small>
            <div className='mcsa_journey_q__tube'>
                <span className='mcsa_journey_q__tube__fill' style={{ height: `${fillH}%`, background: `linear-gradient(to top, ${top}, ${bottom})`, minHeight: value == null ? 0 : 22 }} />
                <div className='mcsa_journey_q__tube__knobs'>
                    {[0, 1, 2, 3].map(index => {
                        const selected = value === index;
                        return (
                            <button key={index} type='button' onClick={() => onPick(index)}>
                                <span
                                    className={selected ? 'is-on' : ''}
                                    style={selected ? { background: `linear-gradient(140deg, ${top}, ${bottom})` } : undefined}
                                />
                            </button>
                        );
                    })}
                </div>
            </div>
            <small>↓ {opts[3]}</small>
            <em>{value != null ? opts[value] : ''}</em>
        </div>
    );
};
